/**
 * Date and time serial numbers.
 *
 * Dates, times, percentages, and currency are all stored as plain numbers plus a
 * number format (see the `CellValue` model in `src/core/types.ts`), so the
 * Format_Manager needs one agreed mapping between a number and a calendar
 * instant. The mapping here is Excel's 1900 date system with the conventional
 * `1899-12-30` epoch: serial 1 is `1900-01-01`, and the fractional part is the
 * time of day. The 1900 leap-year bug is deliberately *not* reproduced — the
 * only place it could be observed is a date before `1900-03-01`, and reproducing
 * it would make every arithmetic identity in the evaluator false by a day.
 *
 * Every function here is pure and works in UTC, so formatting is independent of
 * the browser's time zone. A serial is a calendar position, not an instant on a
 * timeline, which is exactly how spreadsheets treat dates.
 */

/** Milliseconds in one day. */
export const MS_PER_DAY = 86_400_000;

/** Seconds in one day. */
export const SECONDS_PER_DAY = 86_400;

/** UTC milliseconds of serial 0 (`1899-12-30`). */
export const SERIAL_EPOCH_MS = Date.UTC(1899, 11, 30);

/** Serial of `9999-12-31`, the last date a four-digit year can express. */
export const MAX_DATE_SERIAL = 2_958_465;

/** Smallest year accepted from typed input, so `Date.UTC` two-digit-year folding never applies. */
export const MIN_INPUT_YEAR = 1000;

/** Largest year accepted from typed input. */
export const MAX_INPUT_YEAR = 9999;

/** Calendar breakdown of a serial number. */
export interface DateParts {
  year: number;
  /** 1-based, `1` is January. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** True when `(year, month, day)` is a real calendar date inside the input range. */
export function isValidYmd(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < MIN_INPUT_YEAR || year > MAX_INPUT_YEAR) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

/** True when `(hour, minute, second)` is a real time of day. */
export function isValidHms(hour: number, minute: number, second: number): boolean {
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || !Number.isInteger(second)) {
    return false;
  }
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 && second >= 0 && second <= 59;
}

/** Whole-day serial for a calendar date. */
export function serialFromDate(year: number, month: number, day: number): number {
  return (Date.UTC(year, month - 1, day) - SERIAL_EPOCH_MS) / MS_PER_DAY;
}

/** Fraction of a day for a time, in `[0, 1)`. */
export function fractionFromTime(hour: number, minute: number, second: number): number {
  return (hour * 3600 + minute * 60 + second) / SECONDS_PER_DAY;
}

/** Serial for a date and a time of day. */
export function serialFromDateTime(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number {
  return serialFromDate(year, month, day) + fractionFromTime(hour, minute, second);
}

/** True when `serial` lands inside the four-digit-year calendar. */
export function isDisplayableSerial(serial: number): boolean {
  return Number.isFinite(serial) && serial >= 0 && serial < MAX_DATE_SERIAL + 1;
}

/**
 * Calendar breakdown of `serial`, rounded to the nearest second.
 *
 * Rounding can carry the time past midnight (a serial of `x.99999999` is
 * `24:00:00` of day `x`), so the carry is folded into the day rather than
 * reported as hour 24.
 */
export function partsFromSerial(serial: number): DateParts {
  const days = Math.floor(serial);
  let secondsOfDay = Math.round((serial - days) * SECONDS_PER_DAY);
  let dayCarry = 0;
  if (secondsOfDay >= SECONDS_PER_DAY) {
    secondsOfDay -= SECONDS_PER_DAY;
    dayCarry = 1;
  }
  const d = new Date(SERIAL_EPOCH_MS + (days + dayCarry) * MS_PER_DAY);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: Math.floor(secondsOfDay / 3600),
    minute: Math.floor((secondsOfDay % 3600) / 60),
    second: secondsOfDay % 60,
  };
}
